import { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ICalFeedConfiguration } from '../../types/icalFeed';
import { ICalEventsTable } from './ICalEventsTable';
import { formatDate } from '../../utils/formatDate';
import { AlertCircle } from 'lucide-react';

interface ICalFeedPanelProps {
  readonly apartmentId: string;
  readonly feed: ICalFeedConfiguration;
  readonly feedKey: string;
  readonly sourceLabel: string;
  readonly displayUrl: string;
  readonly rowSaveError?: string;
  readonly rowRemoveError?: string;
  readonly rowSyncError?: string;
  readonly isSaving: boolean;
  readonly isRemoving: boolean;
  readonly isSyncing: boolean;
  readonly onChangeUrl: (value: string) => void;
  readonly onSave: () => void;
  readonly onRemove: () => void;
  readonly onSyncNow: () => void;
}

export const ICalFeedPanel = ({
  apartmentId,
  feed,
  feedKey,
  sourceLabel,
  displayUrl,
  rowSaveError,
  rowRemoveError,
  rowSyncError,
  isSaving,
  isRemoving,
  isSyncing,
  onChangeUrl,
  onSave,
  onRemove,
  onSyncNow,
}: ICalFeedPanelProps) => {
  const { t } = useTranslation(['pages', 'forms']);
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        aria-expanded={expanded}
        aria-controls={`ical-feed-panel-${feedKey}`}
        aria-label={`${expanded ? t('pages:icalFeedCollapse') : t('pages:icalFeedExpand')}: ${sourceLabel}`}
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">{sourceLabel}</span>
          {feed.sourceKey && (
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-500 border border-gray-200">
              {feed.sourceKey}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {expanded && (
        <div id={`ical-feed-panel-${feedKey}`} className="px-3 pb-3">
          <div className="mb-3">
            <label
              htmlFor={`ical-feed-url-${apartmentId}-${feedKey}`}
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              {t('forms:icalFeedUrl')}
            </label>
            <input
              id={`ical-feed-url-${apartmentId}-${feedKey}`}
              type="url"
              value={displayUrl}
              onChange={e => onChangeUrl(e.target.value)}
              placeholder={t('forms:icalFeedUrlPlaceholder')}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="mb-3 space-y-1 text-xs text-gray-600">
            {feed.lastSyncedAt ? (
              <p>{t('pages:icalFeedLastSynced', { date: formatDate(feed.lastSyncedAt.split('T')[0]) })}</p>
            ) : (
              <p>{t('pages:icalFeedNeverSynced')}</p>
            )}
            {feed.lastSyncError && (
              <p className="flex items-start gap-1 text-amber-700">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                {t('pages:icalFeedSyncError', { message: feed.lastSyncError })}
              </p>
            )}
          </div>

          {rowSaveError && (
            <p className="mb-2 text-xs text-red-600" role="alert">{rowSaveError}</p>
          )}
          {rowRemoveError && (
            <p className="mb-2 text-xs text-red-600" role="alert">{rowRemoveError}</p>
          )}
          {rowSyncError && (
            <p className="mb-2 text-xs text-red-600" role="alert">{rowSyncError}</p>
          )}

          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? t('pages:savingFeedUrl') : t('pages:saveFeedUrl')}
            </button>

            {feed.status === 'ACTIVE' && (
              <button
                onClick={onSyncNow}
                disabled={isSyncing}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw size={12} className={isSyncing ? 'animate-spin' : undefined} />
                {isSyncing ? t('pages:icalFeedSyncing') : t('pages:icalFeedSyncNow')}
              </button>
            )}

            <button
              onClick={onRemove}
              disabled={isRemoving}
              className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('pages:removeFeed')}
            </button>
          </div>

          <ICalEventsTable
            apartmentId={apartmentId}
            icalFeedConfigurationId={feed.icalFeedConfigurationId}
            sourceKey={feed.sourceKey}
          />
        </div>
      )}
    </div>
  );
};
